import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { getSentryConfig } from "@/lib/sentry-config.functions";
import { getPostHogConfig } from "@/lib/posthog-config.functions";

import { captureUtmParams } from "@/lib/utmParams";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  loader: async () => {
    const [sentryRes, posthogRes] = await Promise.allSettled([
      getSentryConfig(),
      getPostHogConfig(),
    ]);
    const sentry =
      sentryRes.status === "fulfilled"
        ? sentryRes.value
        : { dsn: null, environment: "development" as const };
    if (sentryRes.status === "rejected") {
      console.error("getSentryConfig failed", sentryRes.reason);
    }
    const posthog =
      posthogRes.status === "fulfilled"
        ? posthogRes.value
        : { apiKey: null, host: "https://us.i.posthog.com" };
    if (posthogRes.status === "rejected") {
      console.error("getPostHogConfig failed", posthogRes.reason);
    }
    return { sentry, posthog };
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "KnowMeno — Your menopause tracking companion" },
      { name: "description", content: "KnowMeno helps women understand and track perimenopause and menopause with daily logs, a Menopause Score, and a Symptom Intelligence Report." },
      { name: "author", content: "KnowMeno" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "KnowMeno" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#4B1F4F" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "KnowMeno" },
      { name: "google-site-verification", content: "3WX0NJajnJiDp59pdCmz-GeYr7GkPcSb89_YNnlDS6c" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "KnowMeno",
              url: "https://www.knowmeno.com",
              logo: "https://www.knowmeno.com/icon-512.png",
              sameAs: [
                "https://www.facebook.com/knowmeno",
                "https://www.instagram.com/knowmeno",
              ],
            },
            {
              "@type": "WebSite",
              name: "KnowMeno",
              url: "https://www.knowmeno.com",
            },
          ],
        }),
      },
      {
        src: "https://app.termly.io/resource-blocker/d8d7f41f-38a8-4ca0-adb4-97ed1aae4aa9?autoBlock=on",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Playfair+Display:wght@400;500&display=swap" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=2" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png?v=2" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=2" },
      { rel: "manifest", href: "/manifest.json" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Toaster position="top-center" richColors closeButton />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { sentry, posthog } = Route.useLoaderData();
  useEffect(() => {
    // First-touch UTM capture — runs on any landing path (/, /sign-up, etc.)
    // so ads pointing anywhere on the site attribute correctly. Stored in
    // sessionStorage and forwarded to signup user_metadata + Stripe customer
    // and subscription metadata at checkout time.
    captureUtmParams();

    // Dynamic imports keep client-only modules out of the server bundle.
    import("@/lib/sentry-browser")
      .then(({ initSentryClient }) => initSentryClient(sentry))
      .catch((err) => console.error("sentry import failed", err));

    import("@/lib/posthog-browser")
      .then(({ initPostHogClient }) => initPostHogClient(posthog))
      .catch((err) => console.error("posthog import failed", err));
  }, [sentry, posthog]);
  return (
    <>
      <main>
        <Outlet />
      </main>
    </>
  );
}
